import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getMyRaces,
    createRace,
    updateRace,
    deleteRace,
    type Race,
    type CreateRaceInput,
    type UpdateRaceInput,
} from '../api';

export type { Race, CreateRaceInput, UpdateRaceInput, RaceStatus } from '../api';

const RACES_KEY = ['races'] as const;

// The generic is pinned explicitly: dependencies are installed without a lock
// file, and some @tanstack/react-query versions infer `data` as `unknown`,
// which makes every `races.filter((r) => ...)` call site an implicit-any error.
export const useRaces = () =>
    useQuery<Race[]>({ queryKey: RACES_KEY, queryFn: getMyRaces });

export const useCreateRace = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateRaceInput) => createRace(input),
        onSuccess: () => qc.invalidateQueries({ queryKey: RACES_KEY }),
    });
};

export const useUpdateRace = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: UpdateRaceInput }) =>
            updateRace(id, input),
        onSuccess: () => qc.invalidateQueries({ queryKey: RACES_KEY }),
    });
};

export const useDeleteRace = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteRace(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: RACES_KEY }),
    });
};
